import React, {useEffect, useRef, useState} from "react";
import {useSelector} from "react-redux";
import NameCase from "namecase";
import {executeGet} from "../../Api";

// Recent duplicate-error history, read from the backend's errorlogs (every collision the
// "SOS Portal Duplicate Error Notification" email was sent about), so the admin can still see
// who a notification was about after the flagged payer has been deleted or dismissed.
const STATUS = {
    open: {label: "Open", className: "badge bg-warning text-dark", hint: "The existing payer is still in the portal with this duplicate error."},
    resolved: {label: "Resolved", className: "badge bg-success", hint: "The attempted payer is now in the portal. Nothing to do."},
    existing_deleted_incoming_missing: {
        label: "Missing from portal",
        className: "badge bg-danger",
        hint: "The existing payer was deleted but the attempted payer never arrived. Re-save this payer in your practice management software so it is sent again.",
    },
};

const fmtDate = (d) => (d ? new Date(d).toLocaleString() : "");
const fullName = (p) => NameCase(`${p?.payerFirst || ""} ${p?.payerMiddle || ""} ${p?.payerLast || ""}`.replace(/\s+/g, " ").trim());

const DuplicateHistory = ({subdomain, days = 30}) => {
    const [rows, setRows] = useState(null);
    const [error, setError] = useState(false);
    const mountedRef = useRef(true);
    const seqRef = useRef(0); // only the latest request may commit its result
    // Dismiss dispatches SHOULD_RELOAD_TABLE_DATA (true, then reset to false); a delete sets
    // deleteUserDataSuccess. Refetch on the truthy edge only, so statuses don't go stale.
    const shouldReloadTableData = useSelector((state) => state.auth.shouldReloadTableData);
    const deleteUserDataSuccess = useSelector((state) => state.auth.deleteUserDataSuccess);

    const load = () => {
        const seq = ++seqRef.current;
        const isCurrent = () => mountedRef.current && seq === seqRef.current;
        setRows(null);
        setError(false);
        executeGet(`users/duplicateHistory?${new URLSearchParams({subdomain, days}).toString()}`)
            .then((res) => {
                if (isCurrent()) setRows((res && res.data && res.data.rows) || []);
            })
            .catch((err) => {
                console.error("Error fetching duplicate history:", err?.response?.status || err?.message || err);
                if (isCurrent()) setError(true);
            });
    };

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        load();
    }, [subdomain, days]);

    useEffect(() => {
        if (!shouldReloadTableData && !deleteUserDataSuccess) return;
        load();
    }, [shouldReloadTableData, deleteUserDataSuccess]);

    return (
        <div className="mt-4">
            <h5>Recent duplicate errors (last {days} days)</h5>
            <p className="text-muted mb-2">
                Every duplicate error notification sent for this portal, including ones whose payer has since been deleted or dismissed.
            </p>
            {error && <div className="text-danger">Could not load duplicate history.</div>}
            {!error && rows === null && <div className="spinner-border spinner-border-sm text-primary" role="status"></div>}
            {!error && rows !== null && rows.length === 0 && <div className="text-muted">No duplicate errors in the last {days} days.</div>}
            {!error && rows !== null && rows.length > 0 && (
                <div className="table-responsive">
                    <table className="zui-table zui-table-rounded table-striped table-sm table-font-size">
                        <thead className="tableheader">
                        <tr>
                            <th>Last seen</th>
                            <th>Attempted to add</th>
                            <th>Existing in portal</th>
                            <th>Conflicting contact</th>
                            <th>Attempts</th>
                            <th>Status</th>
                        </tr>
                        </thead>
                        <tbody>
                        {rows.map((r) => {
                            const st = STATUS[r.status] || {label: r.status, className: "badge bg-secondary", hint: ""};
                            return (
                                <tr key={`${r.xmlPayerId}-${r.dbPayerId}`}>
                                    <td>{fmtDate(r.lastSeenAt)}</td>
                                    <td>{fullName(r.incoming)} <span className="text-muted">(ID {r.incoming?.payerId})</span></td>
                                    <td>
                                        {fullName(r.existing)} <span className="text-muted">(ID {r.existing?.payerId})</span>
                                        {r.existing?.deletedAt && <div className="text-muted small">deleted {fmtDate(r.existing.deletedAt)}</div>}
                                        {r.existing?.duplicateDismissedAt && <div className="text-muted small">dismissed {fmtDate(r.existing.duplicateDismissedAt)}</div>}
                                    </td>
                                    <td>{r.conflictingInfo === "payerEmail" ? "Email" : "Cell"}: {r.conflictingValue}</td>
                                    <td>{r.attempts}</td>
                                    <td>
                                        <span className={st.className} title={st.hint}>{st.label}</span>
                                        {r.status === "resolved" && r.resolvedAsPayerId != null && <div className="small text-muted">contact now held by payer ID {r.resolvedAsPayerId}</div>}
                                        {r.status === "existing_deleted_incoming_missing" && <div className="small">{st.hint}</div>}
                                    </td>
                                </tr>
                            );
                        })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default DuplicateHistory;
